// ApiKeyRowActions — the API-keys table's Actions cell: a kebab with the one
// action the key's state allows (an active key can be revoked, a revoked key
// deleted), mirroring ResponseSetRowActions. Both are the same flows the
// selection bar runs, handed down as callbacks. A key with no action in its
// state renders no kebab at all rather than an empty menu.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface ApiKeyRowActionsProps {
  /** Present while the key is active. */
  onRevoke?: () => void;
  /** Present while the key is revoked. */
  onDelete?: () => void;
}

const ApiKeyRowActions = ({ onRevoke, onDelete }: ApiKeyRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!onRevoke && !onDelete) return null;

  // Keep the menu's clicks out of the row beneath it.
  const stop = (e: MouseEvent) => e.stopPropagation();

  const item = (label: string, icon: string, onClick: () => void) => (
    <button
      type='button'
      className='app-dropdown__item app-dropdown__item--danger'
      role='menuitem'
      onClick={() => {
        setOpen(false);
        onClick();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <span className='forms-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={200}
        portal
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('forms_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {onRevoke && item(t('api_keys_action_revoke'), 'ban', onRevoke)}
        {onDelete && item(t('api_keys_action_delete'), 'trash-2', onDelete)}
      </Dropdown>
    </span>
  );
};

export default ApiKeyRowActions;
